import { useMatch, useParams } from 'react-router';
import { usePropertyAssociationContext } from '../propertyAssociation/PropertyAssociationContext';
import { NO_AREA_PLACEHOLDER } from '../propertyAssociation/propertyAssociationEnums';
import { useLastAreaId } from '../propertyAssociation/useLastAreaId';

export type NavigationItemId = 'members' | 'messages' | 'settings' | 'bookings' | 'assets' | 'adverts';

export type NavigationContext = {
    isAdmin: boolean;
    isGuest: boolean;
    isRBHyr: boolean;
};

export type SubNavigationItem = {
    id: string;
    label: string | ((ctx: NavigationContext) => string);
    route: string; // Relative to parent (e.g., 'archive', 'new')
    end?: boolean; // For NavLink end prop
    checkAvailability?: (ctx: NavigationContext) => boolean;
};

export type NavigationItemConfig = {
    id: NavigationItemId;
    label: string;
    iconPath: string;
    route: string;
    requiresArea: boolean;
    checkAvailability?: (ctx: NavigationContext) => boolean;
    subItems?: SubNavigationItem[];
};

export type ComputedSubNavigationItem = {
    id: string;
    label: string;
    path: string;
    end?: boolean;
};

export type ComputedNavigationItem = Omit<NavigationItemConfig, 'subItems'> & {
    path: string;
    available: boolean;
    subItems?: ComputedSubNavigationItem[];
};

// Static configuration - no hooks, pure data
export const navigationConfig: NavigationItemConfig[] = [
    {
        id: 'bookings',
        label: 'Bokningar',
        iconPath: '/bookings',
        route: 'bookings',
        requiresArea: true,
        subItems: [
            { id: 'active', label: 'Aktiva bokningar', route: '', end: true },
            { id: 'archive', label: 'Bokningshistorik', route: 'archive' },
            { id: 'new', label: 'Boka', route: 'new' },
            { id: 'statistics', label: 'Statistik', route: 'statistics' },
        ],
    },
    {
        id: 'assets',
        label: 'Resurser',
        iconPath: '/assets',
        route: 'assets',
        requiresArea: true,
        // Assets submenu is dynamic (lists actual assets), handled separately
    },
    {
        id: 'adverts',
        label: 'Anslagstavlan',
        iconPath: '/adverts',
        route: 'adverts',
        requiresArea: true,
        checkAvailability: ({ isAdmin, isGuest }) => isAdmin || !isGuest,
        subItems: [
            { id: 'user-adverts', label: 'Dela, hyr, sälj', route: 'user-adverts' },
            { id: 'information', label: ({ isRBHyr }) => (isRBHyr ? 'Hyresvärden informerar' : 'Styrelsen informerar'), route: 'information' },
        ],
    },
    {
        id: 'members',
        label: 'Åtkomst',
        iconPath: '/members',
        route: 'members',
        requiresArea: false,
        checkAvailability: ({ isAdmin, isGuest }) => isAdmin || !isGuest,
        subItems: [
            { id: 'active', label: 'Aktiva', route: '', end: true },
            { id: 'pending', label: 'Förfrågningar', route: 'pending' },
        ],
    },
    {
        id: 'messages',
        label: 'Meddelanden',
        iconPath: '/messages',
        route: 'messages',
        requiresArea: false,
        subItems: [
            { id: 'settings', label: 'Chattinställningar', route: 'settings', checkAvailability: ({ isAdmin, isGuest }) => isAdmin || !isGuest },
            { id: 'conversations', label: 'Chattar', route: 'conversations' },
            { id: 'groups', label: 'Hantera chattgrupper', route: 'groups', checkAvailability: ({ isAdmin, isGuest }) => isAdmin || !isGuest },
        ],
    },
    {
        id: 'settings',
        label: 'Inställningar',
        iconPath: '/settings',
        route: 'settings',
        requiresArea: false,
        checkAvailability: ({ isAdmin, isGuest }) => isAdmin || !isGuest,
        subItems: [
            { id: 'admins', label: 'Administratörer', route: 'admins' },
            { id: 'general', label: 'Allmänna inställningar', route: 'general', end: true },
            { id: 'swish', label: 'Swish', route: 'swish' },
            { id: 'apartments', label: 'Lägenheter', route: 'apartments' },
        ],
    },
];

// Hook that computes full paths and availability for main navigation
export const useNavigationItems = () => {
    const { isAdmin, isGuest, isRBHyr, allAreas } = usePropertyAssociationContext();
    const { propertyAssociationId, areaId } = useParams();
    const isOnAreaSelectionPage = !!useMatch('/:propertyAssociationId/areas');

    const { getLastAreaId } = useLastAreaId(propertyAssociationId);
    const lastAreaId = getLastAreaId();

    // Show area-dependent routes when we know which area to use
    const showAreaRoutes = !isOnAreaSelectionPage && (!!areaId || !!lastAreaId || allAreas.length <= 1);

    // Show context-specific menu items when we have a property association
    const hasPropertyAssociation = !!propertyAssociationId;

    // Build paths
    const paBasePath = `/${propertyAssociationId}/`;
    const effectiveAreaId = areaId || lastAreaId || (allAreas.length === 1 ? allAreas[0].propertyAssociationAreaId : NO_AREA_PLACEHOLDER);
    const areaBasePath = `/${propertyAssociationId}/areas/${effectiveAreaId}/`;

    const ctx: NavigationContext = { isAdmin, isGuest, isRBHyr };

    const items: ComputedNavigationItem[] = navigationConfig
        .filter((item) => hasPropertyAssociation && (!item.requiresArea || showAreaRoutes))
        .map((item) => {
            const basePath = item.requiresArea ? areaBasePath : paBasePath;
            const itemPath = `${basePath}${item.route}`;

            return {
                ...item,
                path: itemPath,
                available: item.checkAvailability?.(ctx) ?? true,
                subItems: item.subItems
                    ?.filter((sub) => sub.checkAvailability?.(ctx) ?? true)
                    .map((sub) => ({
                        id: sub.id,
                        label: typeof sub.label === 'function' ? sub.label(ctx) : sub.label,
                        path: sub.route ? `${itemPath}/${sub.route}` : itemPath,
                        end: sub.end,
                    })),
            };
        });

    return { items, showAreaRoutes };
};

// Hook for sub-navigation in specific sections
export const useSubNavigationItems = (parentId: NavigationItemId) => {
    const { items } = useNavigationItems();
    const parent = items.find((item) => item.id === parentId);

    return {
        heading: parent?.label ?? '',
        items: parent?.subItems ?? [],
    };
};
